/**
 * Voice Conversation Prompts Service
 * 
 * Fetches voice conversation prompts from the LLM Query Manager (Firestore).
 * Supports Position 1/2/3 hierarchy:
 *   - Position 1 (Global): Default prompts for all users
 *   - Position 2 (Regional): Override prompts for specific regions
 *   - Position 3 (Local): Override prompts for specific localities
 * 
 * Higher positions trump lower positions (3 > 2 > 1).
 * 
 * NO FALLBACKS for the SC3 path — if Firestore is unavailable, the service unavailable error is shown to user.
 * Symptom Checker 4 may resolve to the SC3 base document when the SC4 `llmQueries` doc has not been created yet.
 */

import { db } from '@healthdesk/shared-services';

// Base Query ID in Firestore (Symptom Checker 3 VoiceConversation + shared SC2 path)
const BASE_QUERY_ID = 'symptom-checker-voice-prompts';

/** Symptom Checker 4 — duplicate process: own `llmQueries` doc; until it exists, SC4 resolves to {@link BASE_QUERY_ID}. */
export const SYMPTOM_CHECKER_V4_VOICE_PROMPTS_QUERY_ID = 'symptom-checker-v4-voice-prompts';

/** /scmap2 fork — own `llmQueries` doc; until cloned, resolves to {@link BASE_QUERY_ID} (same as /scmap). */
export const SYMPTOM_CHECKER_SCMAP2_VOICE_PROMPTS_QUERY_ID =
  'symptom-checker-voice-prompts-scmap2';

// Collection name (matches queryRegistryService)
const QUERIES_COLLECTION = 'llmQueries';

/** Cache key → { prompts, timestamp } */
const promptsCacheByKey = new Map();
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/** Single in-flight fetch per base id + region + locality so remounts don't stack parallel Firestore reads. */
const inflightFetches = new Map();

/**
 * @param {string} baseQueryId
 * @param {1|2|3} position
 */
function getDocIdForBase(baseQueryId, position, region = null, locality = null) {
  if (position === 1) return baseQueryId;
  if (position === 2 && region) return `${baseQueryId}-${region}`;
  if (position === 3 && locality) return `${baseQueryId}-${locality}`;
  return baseQueryId;
}

/**
 * SC4 uses its own `llmQueries` doc when present; otherwise the same merged prompts as SC3
 * {@link BASE_QUERY_ID} so operators can fork copy in Firestore without a code deploy.
 *
 * @returns {Promise<string>}
 */
export async function resolveSc4VoicePromptsBaseQueryId() {
  return resolveVoicePromptsBaseQueryIdWithFallback(SYMPTOM_CHECKER_V4_VOICE_PROMPTS_QUERY_ID);
}

/**
 * Fork-safe voice-prompts resolution: use the requested `llmQueries` doc when present,
 * otherwise fall back to the canonical SC3/SC2 base so new routes (e.g. /scmap2) work
 * before operators clone prompts in Query Manager.
 *
 * @param {string} requestedBaseQueryId
 * @returns {Promise<string>}
 */
export async function resolveVoicePromptsBaseQueryIdWithFallback(requestedBaseQueryId) {
  if (!requestedBaseQueryId || requestedBaseQueryId === BASE_QUERY_ID) {
    return BASE_QUERY_ID;
  }
  const snap = await db.collection(QUERIES_COLLECTION).doc(requestedBaseQueryId).get();
  if (snap.exists) return requestedBaseQueryId;
  console.info(
    "[VoicePromptsService] llmQueries/%s not found — using VoiceConversation template (%s) until the fork doc is created.",
    requestedBaseQueryId,
    BASE_QUERY_ID
  );
  return BASE_QUERY_ID;
}

/**
 * Default prompt structure - used when creating the initial Firestore document
 * This is NOT a fallback - it's the seed data for the Query Manager
 */
export const DEFAULT_VOICE_PROMPTS = {
  // =============================================================================
  // OPENING & CLOSING
  // =============================================================================
  opening: "Hi! What seems to be the problem today?",
  medicolegalDisclaimer:
    'This tool provides general health information only. It does not diagnose, treat, or triage emergencies. It is not a substitute for care from a licensed clinician. For emergencies, use local emergency services.',
  closing: "Thank you for providing all that information. I now have a good understanding of your symptoms. Please review the computational analysis on the right.",
  closingMaxReached: "Thank you for providing all that information. I now have a good understanding of your symptoms. Please review the analysis, but remember to consult a healthcare provider for proper diagnosis and treatment.",
  stillListening: "I'm still here when you're ready. Take your time.",
  
  // =============================================================================
  // PREREQUISITE QUESTIONS - Asked before LLM clarifying questions
  // =============================================================================
  prerequisiteQuestions: [
    "First, could you tell me your age and whether you're male or female?",
    "How long have you been experiencing the {chiefComplaint}?",
    "Are there any other symptoms you're experiencing along with this? Please share anything else you've noticed, even if it seems unrelated.",
    "And how long have you had these other symptoms?",
  ],
  
  // Question indices for conditional logic
  prerequisiteIndices: {
    demographics: 0,
    durationChief: 1,
    associatedSymptoms: 2,
    durationAssociated: 3,
  },
  
  // Clarification when chief complaint is unclear
  chiefComplaintClarification: "I want to make sure I understand - what would you say is your main concern or the symptom that's bothering you the most?",
  
  // =============================================================================
  // PROXY MODE - For parents/guardians describing symptoms for minors
  // =============================================================================
  minorUserMessage: "I'm sorry, but this symptom checker is designed for adults 18 and older. If you're under 18, please have a parent or guardian use the system on your behalf. They can describe your symptoms and get guidance for you.",
  proxyConfirmationQuestion: "I understand you're concerned about someone else. Are you the parent or guardian of the person you're describing symptoms for?",
  minorDetectedQuestion: "I noticed you mentioned being under 18. Are you describing your own symptoms, or are you a parent or guardian speaking for someone else?",
  proxyDemographicQuestion: "Could you tell me the patient's age and whether they're male or female?",
  
  // Proxy mode question variants (use "the patient" / "they" instead of "you")
  proxyPrerequisiteQuestions: [
    "Could you tell me the patient's age and whether they're male or female?",
    "How long has the patient been experiencing the {chiefComplaint}?",
    "Are there any other symptoms the patient is experiencing? Please share anything you've noticed, even if it seems unrelated.",
    "And how long have they had these other symptoms?",
  ],
  
  proxyPrerequisiteIndices: {
    demographics: 0,
    durationChief: 1,
    associatedSymptoms: 2,
    durationAssociated: 3,
  },
  
  // Patterns indicating proxy situation from chief complaint
  proxyIndicators: [
    'my baby', 'my child', 'my son', 'my daughter', 'my kid',
    'my infant', 'my toddler', 'my newborn', 'my teenager',
    'my husband', 'my wife', 'my mother', 'my father', 'my mom', 'my dad',
    'my grandmother', 'my grandfather', 'my grandma', 'my grandpa',
    'my partner', 'my spouse', 'my parent',
  ],
  
  // =============================================================================
  // FALLBACK SYMPTOM QUESTIONS - Used if LLM hasn't returned questions yet
  // =============================================================================
  fallbackSymptomQuestions: [
    "On a scale of 1 to 10, how severe would you say it is?",
    "Is there anything that makes it better or worse?",
    "Have you had this problem before?",
    "Have you taken any medications or treatments for this?",
  ],
  
  // =============================================================================
  // REFINEMENT TRANSITION - User-controlled transition to diagnosis-specific questions
  // =============================================================================
  // Asked after prerequisites are complete and initial DDX is available
  // Allows user to opt into or out of additional refinement questions
  refinementTransitionQuestion: "Based on what you've told me, I have some ideas about possible conditions. Would you like to answer a few more questions to help narrow things down?",
};

/**
 * Deep merge two objects, with source values taking precedence
 * Only merges non-empty values from source
 */
function deepMerge(target, source) {
  if (!source) return target;
  if (!target) return source;
  
  const result = { ...target };
  
  for (const key of Object.keys(source)) {
    const sourceValue = source[key];
    
    // Skip empty/null/undefined values
    if (sourceValue === null || sourceValue === undefined || sourceValue === '') {
      continue;
    }
    
    // For arrays, only override if source array has content
    if (Array.isArray(sourceValue)) {
      if (sourceValue.length > 0 && sourceValue.some(v => v !== null && v !== undefined && v !== '')) {
        result[key] = sourceValue;
      }
      continue;
    }
    
    // For objects (but not arrays), recursively merge
    if (typeof sourceValue === 'object') {
      result[key] = deepMerge(result[key] || {}, sourceValue);
      continue;
    }
    
    // For primitives, override
    result[key] = sourceValue;
  }
  
  return result;
}

/**
 * Fetch voice conversation prompts from Firestore with position hierarchy.
 *
 * @param {string} baseQueryId - `llmQueries` document id for Position 1 (e.g. {@link BASE_QUERY_ID} or {@link SYMPTOM_CHECKER_V4_VOICE_PROMPTS_QUERY_ID})
 * @param {Object} options
 * @param {string} [options.region]
 * @param {string} [options.locality]
 * @returns {Promise<Object>} Merged prompts object (highest position wins)
 */
export async function fetchVoiceConversationPromptsForBase(baseQueryId, options = {}) {
  const { region, locality } = options;
  if (!baseQueryId || typeof baseQueryId !== "string") {
    throw new Error("Voice prompts base query id is required.");
  }
  const currentCacheKey = `${baseQueryId}::${region || "none"}::${locality || "none"}`;

  const cached = promptsCacheByKey.get(currentCacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
    return cached.prompts;
  }

  const existing = inflightFetches.get(currentCacheKey);
  if (existing) return existing;

  const work = fetchVoiceConversationPromptsInternal(baseQueryId, options, currentCacheKey);
  inflightFetches.set(currentCacheKey, work);
  return work;
}

/**
 * Fetch voice conversation prompts from Firestore with position hierarchy
 *
 * @param {Object} options - Options for fetching
 * @param {string} options.region - User's region for Position 2 lookup
 * @param {string} options.locality - User's locality for Position 3 lookup
 * @returns {Promise<Object>} Merged prompts object (highest position wins)
 * @throws {Error} If Firestore is unavailable or Position 1 doesn't exist
 */
export async function fetchVoiceConversationPrompts(options = {}) {
  return fetchVoiceConversationPromptsForBase(BASE_QUERY_ID, options);
}

async function fetchVoiceConversationPromptsInternal(baseQueryId, options, currentCacheKey) {
  const { region, locality } = options;
  try {
    // Fetch Position 1 (Global) - Required
    const pos1Ref = db.collection(QUERIES_COLLECTION).doc(getDocIdForBase(baseQueryId, 1));
    const pos1Snap = await pos1Ref.get();

    if (!pos1Snap.exists) {
      console.error(`[VoicePromptsService] ❌ Position 1 prompts not found in Firestore (${baseQueryId})`);
      throw new Error("Voice conversation prompts not configured. Please contact support.");
    }

    const pos1Data = pos1Snap.data();
    let mergedPrompts = pos1Data.prompts;

    if (!mergedPrompts || typeof mergedPrompts !== "object") {
      console.error("[VoicePromptsService] ❌ Invalid prompts structure in Position 1");
      throw new Error("Voice conversation prompts are misconfigured. Please contact support.");
    }

    // Fetch Position 2 (Regional) - Optional
    if (region) {
      try {
        const pos2Ref = db.collection(QUERIES_COLLECTION).doc(getDocIdForBase(baseQueryId, 2, region));
        const pos2Snap = await pos2Ref.get();

        if (pos2Snap.exists) {
          const pos2Data = pos2Snap.data();
          if (pos2Data.prompts && typeof pos2Data.prompts === "object") {
            mergedPrompts = deepMerge(mergedPrompts, pos2Data.prompts);
          }
        }
      } catch (pos2Error) {
        console.warn("[VoicePromptsService] ⚠️ Failed to load Position 2, continuing with Position 1:", pos2Error);
      }
    }

    // Fetch Position 3 (Local) - Optional
    if (locality) {
      try {
        const pos3Ref = db.collection(QUERIES_COLLECTION).doc(getDocIdForBase(baseQueryId, 3, null, locality));
        const pos3Snap = await pos3Ref.get();

        if (pos3Snap.exists) {
          const pos3Data = pos3Snap.data();
          if (pos3Data.prompts && typeof pos3Data.prompts === "object") {
            mergedPrompts = deepMerge(mergedPrompts, pos3Data.prompts);
          }
        }
      } catch (pos3Error) {
        console.warn("[VoicePromptsService] ⚠️ Failed to load Position 3, continuing with merged positions:", pos3Error);
      }
    }

    // Validate essential prompts exist after merge
    if (!mergedPrompts.opening || !mergedPrompts.prerequisiteQuestions) {
      console.error("[VoicePromptsService] ❌ Missing essential prompts after merge");
      throw new Error("Voice conversation prompts are incomplete. Please contact support.");
    }

    promptsCacheByKey.set(currentCacheKey, { prompts: mergedPrompts, timestamp: Date.now() });

    return mergedPrompts;
  } catch (error) {
    promptsCacheByKey.delete(currentCacheKey);

    // Re-throw with user-friendly message if it's a Firebase error
    if (error.code === "permission-denied") {
      throw new Error("Unable to load symptom checker. Please try again later.");
    }
    if (error.code === "unavailable") {
      throw new Error("Symptom checker service is temporarily unavailable. Please try again later.");
    }

    // If it's our custom error, re-throw as-is
    if (error.message.includes("Please contact support") || error.message.includes("Please try again")) {
      throw error;
    }

    // Generic error
    console.error("[VoicePromptsService] Firestore error:", error);
    throw new Error("Unable to load symptom checker. Please try again later.");
  } finally {
    inflightFetches.delete(currentCacheKey);
  }
}

/**
 * Check if voice conversation prompts are available (preload check)
 * 
 * @param {Object} options - Options for checking
 * @returns {Promise<boolean>} True if prompts are available
 */
export async function checkPromptsAvailable(options = {}) {
  try {
    await fetchVoiceConversationPrompts(options);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Clear the prompts cache (useful for testing or force refresh)
 */
export function clearPromptsCache() {
  promptsCacheByKey.clear();
  inflightFetches.clear();
}

/**
 * Initialize the voice prompts query in Firestore if it doesn't exist
 * This should be called by an admin/setup process, not during normal operation
 * 
 * @param {string} userId - ID of user creating the query
 * @returns {Promise<void>}
 */
export async function initializeVoicePromptsQuery(userId) {
  const docRef = db.collection(QUERIES_COLLECTION).doc(BASE_QUERY_ID);
  const docSnap = await docRef.get();

  if (docSnap.exists) {
    return;
  }


  const queryDoc = {
    id: BASE_QUERY_ID,
    featureName: 'Symptom Checker - Voice Conversation Prompts',
    featureDescription: 'Global default prompts for the voice conversation flow. Can be overridden by regional (Position 2) or local (Position 3) prompts.',
    category: 'symptom_checker',
    isStaticContent: true,
    
    // Store all prompts in a structured object
    prompts: DEFAULT_VOICE_PROMPTS,
    
    // Chain configuration - Position 1 (Global)
    chainConfig: {
      isPartOfChain: true,
      chainId: BASE_QUERY_ID,
      chainPosition: 1,
    },
    
    // Not an LLM call, just prompt storage
    model: 'none',
    temperature: 0,
    maxTokens: 0,
    
    geographicScope: 'global',
    
    uiLocation: {
      component: 'VoiceConversation',
      file: 'features/patient/components/symptomCheck/VoiceConversation.jsx',
    },
    
    createdAt: new Date(),
    updatedAt: new Date(),
    createdBy: userId || 'system',
    lastEditedBy: userId || 'system',
    currentVersion: 1,
    
    stats: {
      totalCalls: 0,
      avgResponseTime: 0,
      avgCost: 0,
      successRate: 0,
      lastUsed: null,
    },
  };

  await docRef.set(queryDoc);
}

// Export the base query ID for reference
export const VOICE_PROMPTS_QUERY_ID = BASE_QUERY_ID;
